import { useState, useCallback } from "react";
import type { TreeFolder } from "../types";
import { useToast } from "@/hooks/use-toast";
import {
  extractDroppedFiles,
  isEmptySelection,
  type UploadSelection,
} from "../utils/uploadSelection";
import {
  isAppMoveDragInProgress,
  isEligibleMoveDropTarget,
  isIdList,
  isWritableFolderDropTarget,
  wouldCreateCircularFolderMove,
} from "../utils/documentMoveDrag";
import { hasExternalFiles } from "../utils/dragEvents";

/**
 * True when the pointer has actually left the element's box. `dragleave` also
 * fires when the pointer crosses into a child, so the highlight must only be
 * cleared when the coordinates fall outside the element's own rect.
 */
const hasLeftElementBounds = (e: React.DragEvent) => {
  const rect = e.currentTarget.getBoundingClientRect();
  return (
    e.clientX < rect.left ||
    e.clientX > rect.right ||
    e.clientY < rect.top ||
    e.clientY > rect.bottom
  );
};

/**
 * Callbacks for handling different types of drops
 */
export interface FolderDragDropCallbacks {
  onDropDocument?: (documentId: string, targetFolderId: string | null) => void;
  onMoveFolder?: (folderId: string, targetFolderId: string | null) => void;
  onDropSelection?: (docIds: string[], folderIds: string[], targetFolderId: string | null) => void;
  /**
   * Called with OS files released directly on a folder target, routing the
   * upload into THAT folder. When omitted, external file drags are left alone
   * entirely so they bubble to the page-level upload zone (which uploads into
   * the currently open folder).
   */
  onDropFiles?: (selection: UploadSelection, targetFolderId: string | null) => void;
  /**
   * Called when the pointer settles on a folder this target will accept, so a
   * tree view can reveal that folder's children mid-drag — a collapsed parent
   * is otherwise a dead end, since a child that is never rendered can never be
   * dropped on. The dwell timer lives with the expansion state it mutates:
   * pass `startHoverExpand` / `cancelHoverExpand` from `useFolderExpansion`.
   * Views that render folders as a flat list have nothing to expand and wire
   * neither.
   */
  onHoverExpand?: (folderId: string, hasChildren: boolean) => void;
  /** Ends the dwell above — the drag left the folder, or the drop happened. */
  onCancelHoverExpand?: () => void;
}

/**
 * Return value from useFolderDragDrop hook
 */
export interface FolderDragDropResult {
  dragOverId: string | null;
  isDraggedOver: (folderId: string) => boolean;
  handleDragEnter: (folderId: string, hasChildren?: boolean) => (e: React.DragEvent) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (folderId: string | null) => (e: React.DragEvent) => void;
  handleDragOver: (folderId: string | null) => (e: React.DragEvent) => void;
}

/**
 * Hook for centralized drag/drop logic with circular move prevention
 *
 * Consolidates all drag/drop behavior for folders and documents, including:
 * - Circular move prevention (can't move folder into itself or descendants)
 * - Multi-selection support (drag multiple docs/folders at once)
 * - Visual feedback (dragOverId state)
 * - Proper cleanup of hover timers
 * - Boundary checking for drag leave events
 *
 * This hook eliminates ~150 lines of duplicated code across components.
 *
 * @param folders - Tree of all folders (for circular reference checking)
 * @param callbacks - Callbacks for different drop actions
 * @param autoExpandEnabled - Whether a sustained hover reveals a folder's
 *   children (default: true). Only does anything when the host also wires
 *   `onHoverExpand` / `onCancelHoverExpand`.
 * @returns Drag/drop handlers and state
 *
 * @example
 * ```tsx
 * const { dragOverId, handleDragEnter, handleDragLeave, handleDrop, handleDragOver } =
 *   useFolderDragDrop(folders, {
 *     onDropDocument: (docId, folderId) => console.log('Drop doc', docId, 'to', folderId),
 *     onMoveFolder: (folderId, targetId) => console.log('Move folder', folderId, 'to', targetId),
 *   });
 *
 * return (
 *   <div
 *     onDragOver={handleDragOver(folder.id)}
 *     onDragEnter={handleDragEnter(folder.id, hasChildren)}
 *     onDragLeave={handleDragLeave}
 *     onDrop={handleDrop(folder.id)}
 *     className={dragOverId === folder.id ? 'drag-over' : ''}
 *   >
 *     {folder.name}
 *   </div>
 * );
 * ```
 */
export function useFolderDragDrop(
  folders: TreeFolder[],
  callbacks: FolderDragDropCallbacks,
  autoExpandEnabled = true,
  sourceFolderIds: string[] = []
) {
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const { toast } = useToast();

  const { onHoverExpand, onCancelHoverExpand } = callbacks;

  /**
   * Start (or restart) the dwell on a folder the pointer just entered, and end
   * it on anything that ends the hover — a refused target, a drag leaving the
   * row, the drop itself. The timer belongs to the host's expansion state, so
   * this only forwards; unmounting the host cancels it there.
   */
  const hoverExpand = useCallback(
    (folderId: string, hasChildren: boolean) => {
      if (!autoExpandEnabled) return;
      onHoverExpand?.(folderId, hasChildren);
    },
    [autoExpandEnabled, onHoverExpand]
  );
  const cancelHoverExpand = useCallback(() => onCancelHoverExpand?.(), [onCancelHoverExpand]);

  const isDraggedOver = useCallback((folderId: string) => dragOverId === folderId, [dragOverId]);

  /**
   * A move payload is only ours if this document started the drag — the MIME
   * type alone is forgeable by any page the user might drag from.
   */
  const acceptsAnyMovePayload = useCallback(
    (dataTransfer: DataTransfer) =>
      isAppMoveDragInProgress() &&
      ((dataTransfer.types.includes("application/selection") && !!callbacks.onDropSelection) ||
        (dataTransfer.types.includes("application/doc-id") && !!callbacks.onDropDocument) ||
        (dataTransfer.types.includes("application/folder-id") && !!callbacks.onMoveFolder)),
    [callbacks.onDropDocument, callbacks.onDropSelection, callbacks.onMoveFolder]
  );

  const acceptsMoveDrop = useCallback(
    (dataTransfer: DataTransfer, folderId: string | null) =>
      acceptsAnyMovePayload(dataTransfer) &&
      isEligibleMoveDropTarget(folders, folderId, sourceFolderIds),
    [acceptsAnyMovePayload, folders, sourceFolderIds]
  );

  /**
   * True when an external OS-file drag should be handled by THIS target rather
   * than left to the page-level upload zone: the host wired onDropFiles and
   * the target is a real, writable folder, or the project root (null, or the
   * "root" highlight sentinel root items paint with). Container pseudo-folders
   * and the locked system folder never advertise themselves — drops there
   * bubble to the zone. Ids the current tree can't resolve (stale or empty
   * tree mid-refetch) are refused too: the hook can't verify they're writable.
   */
  const acceptsFileDrop = useCallback(
    (dataTransfer: DataTransfer, folderId: string | null) => {
      if (!callbacks.onDropFiles || !hasExternalFiles(dataTransfer)) return false;
      return isWritableFolderDropTarget(folders, folderId);
    },
    [callbacks.onDropFiles, folders]
  );

  const handleDragEnter = useCallback(
    (folderId: string, hasChildren = false) =>
      (e: React.DragEvent) => {
        // A move payload is consumed by the innermost row it reaches, whether
        // or not that row can take it. Rows nest (a tree node's wrapper
        // contains its children), so letting an ineligible row's event bubble
        // would highlight — and, via handleDragOver/handleDrop, move into — an
        // ancestor folder the pointer is nowhere near.
        if (acceptsAnyMovePayload(e.dataTransfer)) {
          e.preventDefault();
          e.stopPropagation();
          const eligible = acceptsMoveDrop(e.dataTransfer, folderId);
          setDragOverId(eligible ? folderId : null);
          // A folder that refuses the move — the drag's own source, or one of
          // its descendants — is worth nothing expanded: every folder under it
          // refuses the same drop.
          if (eligible) hoverExpand(folderId, hasChildren);
          else cancelHoverExpand();
          return;
        }
        // External drags drop into the page-level upload zone, not this folder
        // — unless the host routes file drops here. Don't paint a target or
        // block the event otherwise.
        if (!acceptsFileDrop(e.dataTransfer, folderId)) return;
        e.preventDefault();
        e.stopPropagation();
        setDragOverId(folderId);
        hoverExpand(folderId, hasChildren);
      },
    [acceptsAnyMovePayload, acceptsFileDrop, acceptsMoveDrop, cancelHoverExpand, hoverExpand]
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      const isMove = acceptsAnyMovePayload(e.dataTransfer);
      const isRoutedFiles = !!callbacks.onDropFiles && hasExternalFiles(e.dataTransfer);
      // Neither ours: nothing was painted, and swallowing the event would hide
      // the final dragleave from the page-level upload zone (stuck highlight).
      if (!isMove && !isRoutedFiles) return;
      // File drags must keep bubbling for the same reason — the upload zone
      // clears its own highlight on this dragleave.
      if (isMove) {
        e.preventDefault();
        e.stopPropagation();
      }

      // Only clear if we're actually leaving this element (boundary check)
      if (!hasLeftElementBounds(e)) return;

      setDragOverId(null);
      cancelHoverExpand();
    },
    [acceptsAnyMovePayload, callbacks.onDropFiles, cancelHoverExpand]
  );

  const handleDrop = useCallback(
    (targetFolderId: string | null) => (e: React.DragEvent) => {
      // Drop ends the drag either way — clear highlight and hover timer even
      // when the event isn't ours (dragleave never fires on the drop target).
      setDragOverId(null);
      cancelHoverExpand();

      if (!acceptsAnyMovePayload(e.dataTransfer)) {
        // Routed external file drop: this target advertised itself during
        // dragenter, so take the drop here — otherwise the page-level upload
        // zone would upload into the currently open folder, not the folder
        // the user visibly dropped on.
        if (acceptsFileDrop(e.dataTransfer, targetFolderId)) {
          e.preventDefault();
          e.stopPropagation();
          // The drop is consumed (stopPropagation above), so the page-level
          // zone can't recover it — failures past this point must be told to
          // the user, not just logged.
          extractDroppedFiles(e.dataTransfer)
            .then((selection) => {
              if (!isEmptySelection(selection)) {
                callbacks.onDropFiles?.(selection, targetFolderId);
              } else {
                toast({
                  title: "Drop failed",
                  description: "No readable files were found in the dropped items.",
                  variant: "destructive",
                });
              }
            })
            .catch((error) => {
              console.error("Failed to read dropped files:", error);
              toast({
                title: "Drop failed",
                description: "Failed to read the dropped files. Please try again.",
                variant: "destructive",
              });
            });
          return;
        }
        // Not ours (text drag, or a non-writable pseudo-folder target):
        // returning without stopPropagation lets it reach the upload zone.
        return;
      }
      e.preventDefault();
      e.stopPropagation();

      // Refused here, and consumed rather than passed on: the row under the
      // pointer is the only row entitled to this move, so an ineligible one
      // ends the drag instead of handing it to an ancestor folder.
      if (!acceptsMoveDrop(e.dataTransfer, targetFolderId)) return;

      const docId = e.dataTransfer.getData("application/doc-id");
      const folderId = e.dataTransfer.getData("application/folder-id");
      const selectionData = e.dataTransfer.getData("application/selection");

      // Priority: Multi-selection > Document > Folder
      if (selectionData && callbacks.onDropSelection) {
        try {
          const parsed = JSON.parse(selectionData) as {
            docIds?: unknown;
            folderIds?: unknown;
          } | null;
          const docIds = parsed?.docIds;
          const folderIds = parsed?.folderIds;
          // Checked down to the elements: a malformed list would otherwise
          // reach the repository and come back as a PostgREST failure the user
          // reads as "the move broke", rather than a drop that was never ours.
          if (!isIdList(docIds) || !isIdList(folderIds)) {
            throw new Error("Invalid selection payload");
          }
          if (wouldCreateCircularFolderMove(folders, folderIds, targetFolderId)) {
            toast({
              title: "Cannot move folder",
              description: "Cannot move a folder into itself or its descendants.",
              variant: "destructive",
            });
            return;
          }
          callbacks.onDropSelection(docIds, folderIds, targetFolderId);
        } catch (error) {
          console.error("Failed to parse selection data:", error);
          toast({
            title: "Drop failed",
            description: "Failed to process the dropped items. Please try again.",
            variant: "destructive",
          });
        }
      } else if (docId && callbacks.onDropDocument) {
        callbacks.onDropDocument(docId, targetFolderId);
      } else if (folderId && callbacks.onMoveFolder && folderId !== targetFolderId) {
        // Prevent moving folder into itself or its descendants
        if (targetFolderId) {
          const isCircular = wouldCreateCircularFolderMove(folders, [folderId], targetFolderId);
          if (isCircular) {
            toast({
              title: "Cannot move folder",
              description: "Cannot move a folder into itself or its descendants.",
              variant: "destructive",
            });
            return;
          }
        }
        callbacks.onMoveFolder(folderId, targetFolderId);
      }
    },
    [
      acceptsAnyMovePayload,
      acceptsFileDrop,
      acceptsMoveDrop,
      callbacks,
      cancelHoverExpand,
      folders,
      toast,
    ]
  );

  const handleDragOver = useCallback(
    (folderId: string | null) => (e: React.DragEvent) => {
      if (acceptsAnyMovePayload(e.dataTransfer)) {
        // Same containment as dragenter: an ancestor row must never accept the
        // drop for a nested row that refused it. No preventDefault when
        // refused — the browser then paints "no drop" and fires no drop event.
        e.stopPropagation();
        if (acceptsMoveDrop(e.dataTransfer, folderId)) {
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
        } else {
          e.dataTransfer.dropEffect = "none";
        }
      } else if (acceptsFileDrop(e.dataTransfer, folderId)) {
        // Only advertise targets handleDrop will actually take. Accepting
        // dragover on a refused target (container/system row) forces the drop
        // to fire there; with the page-level zone disabled (system folder
        // open) nothing would preventDefault it and the browser would
        // navigate the tab to the dropped file.
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
      }
    },
    [acceptsAnyMovePayload, acceptsFileDrop, acceptsMoveDrop]
  );

  return {
    dragOverId,
    isDraggedOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    handleDragOver,
  };
}
